import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import WorkGrid from "@/components/feature/WorkGrid";
import { works } from "@/mocks/works";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  isValidType,
  sortWorks,
  typeLabelMap,
  typeDescMap,
  type SortKey,
} from "@/utils/work";

const PAGE_SIZE = 8;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新发布" },
  { key: "popular", label: "热度" },
  { key: "rating", label: "评分" },
];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const valid = isValidType(type);

  const [year, setYear] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [sortBy, setSortBy] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const typeWorks = useMemo(
    () => (valid ? works.filter((item) => item.type === type) : []),
    [valid, type]
  );

  const years = useMemo(
    () => ["全部", ...Array.from(new Set(typeWorks.map((w) => String(w.year)))).sort((a, b) => Number(b) - Number(a))],
    [typeWorks]
  );

  const regions = useMemo(
    () => ["全部", ...Array.from(new Set(typeWorks.map((w) => w.region)))],
    [typeWorks]
  );

  const filtered = useMemo(() => {
    const list = typeWorks.filter((work) => {
      const yearOk = year === "全部" || String(work.year) === year;
      const regionOk = region === "全部" || work.region === region;
      return yearOk && regionOk;
    });
    return sortWorks(list, sortBy);
  }, [typeWorks, year, region, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [year, region, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setYear("全部");
    setRegion("全部");
    setSortBy("latest");
  };

  const metaTitle = valid
    ? `${typeLabelMap[type]} - 奇米国产自拍国产自制影视作品分类`
    : "分类 - 国产自拍";
  const metaDesc = valid
    ? `${typeDescMap[type]}支持按年份与地区筛选，实时查看最新国产自制${typeLabelMap[type]}内容。`
    : undefined;

  usePageMeta({
    title: metaTitle,
    description: metaDesc,
    keywords: valid ? `国产自拍,国产${typeLabelMap[type]},自制剧,${typeLabelMap[type]}推荐` : undefined,
  });

  if (!valid) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <p className="mt-4 text-base text-foreground-700">
          未找到该分类，请从下方选择合适的分类浏览
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/category/movie"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
          >
            去电影
          </Link>
          <Link
            to="/"
            className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const label = typeLabelMap[type];

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label }]} />

      <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-line text-2xl" />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {label}
            </h1>
            <p className="mt-1 max-w-2xl text-xs text-foreground-600 md:text-sm">
              {typeDescMap[type]}
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3 border-t border-background-200 pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-medium text-foreground-500 md:text-sm">年份</span>
            {years.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setYear(item)}
                className={`rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                  year === item
                    ? "bg-accent-500 font-medium text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-accent-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-medium text-foreground-500 md:text-sm">地区</span>
            {regions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setRegion(item)}
                className={`rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                  region === item
                    ? "bg-accent-500 font-medium text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-accent-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <span className="font-bold text-accent-600">{filtered.length}</span> 部作品
        </p>
        <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSortBy(option.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                sortBy === option.key
                  ? "bg-primary-500 font-medium text-background-50"
                  : "text-foreground-700 hover:text-accent-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <WorkGrid
          works={paged}
          emptyText="暂无符合筛选条件的影视作品"
          emptyAction={
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          }
        />
      </div>

      <Pagination current={page} total={totalPages} onChange={setPage} />
    </div>
  );
}